import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface OpeningHours {
  open: string;
  close: string;
  closed: boolean;
}

interface VenueData {
  id: string;
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  pricePerHour: number;
  operatorId: string;
  operatorName: string;
  machineCount: number;
  tableCount: number;
  status: 'active' | 'inactive';
  isActive: boolean;
  openingHours: {
    monday: OpeningHours;
    tuesday: OpeningHours;
    wednesday: OpeningHours;
    thursday: OpeningHours;
    friday: OpeningHours;
    saturday: OpeningHours;
    sunday: OpeningHours;
  };
  createdAt: string;
}

const VenueManagement: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isVenueOperator, user } = useAuth();
  const [venues, setVenues] = useState<VenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState<VenueData | null>(null);
  const [newVenue, setNewVenue] = useState({
    name: '',
    address: '',
    city: '',
    pricePerHour: 0,
    operatorName: '',
    machineCount: 0,
    tableCount: 0,
    openingHours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '20:00', closed: false }
    }
  });
  const [userVenueId, setUserVenueId] = useState<string | null>(null);

  useEffect(() => {
    loadUserVenueId();
  }, [user]);

  useEffect(() => {
    if (isAdmin || (isVenueOperator && userVenueId)) {
      loadVenues();
    }
  }, [userVenueId, isAdmin, isVenueOperator]);

  const loadUserVenueId = async () => {
    if (!user || !isVenueOperator) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData?.venueId) {
          setUserVenueId(userData.venueId);
        }
      }
    } catch (error) {
      console.error('Error loading user venue ID:', error);
    }
  };

  const loadVenues = async () => {
    try {
      setLoading(true);
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      let venuesData: VenueData[] = [];

      for (const venueDoc of venuesSnapshot.docs) {
        const venueData = venueDoc.data();

        // Skip deleted venues
        if (venueData.isActive === false) {
          continue;
        }

        // Fetch operator name from users collection
        let operatorName = venueData.operatorName || 'Ismeretlen';
        if (venueData.operatorId) {
          try {
            const userDocRef = doc(db, 'users', venueData.operatorId);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              operatorName = userData.displayName || userData.username || userData.email || 'Ismeretlen';
            }
          } catch (err) {
            console.error('Error fetching operator name:', err);
          }
        }

        // Count boards for this venue
        let boardCount = 0;
        try {
          const boardsSnapshot = await getDocs(collection(db, 'boards'));
          boardCount = boardsSnapshot.docs.filter(doc => doc.data().venueId === venueDoc.id).length;
        } catch (err) {
          console.error('Error counting boards:', err);
        }

        venuesData.push({
          id: venueDoc.id,
          ...venueData,
          operatorName,
          tableCount: boardCount
        } as VenueData);
      }

      // If current user is a venue operator (not admin), filter to only show their assigned venue
      if (isVenueOperator && !isAdmin && userVenueId) {
        venuesData = venuesData.filter(venue => venue.id === userVenueId);
      }

      setVenues(venuesData);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const addVenue = async () => {
    // Validation
    if (!newVenue.name || !newVenue.address || !newVenue.city) {
      alert('Név, cím és város megadása kötelező!');
      return;
    }
    if (!newVenue.pricePerHour || newVenue.pricePerHour <= 0) {
      alert('Óránkénti ár megadása kötelező és pozitív szám kell legyen!');
      return;
    }

    try {
      await addDoc(collection(db, 'venues'), {
        name: newVenue.name,
        address: newVenue.address,
        city: newVenue.city,
        pricePerHour: newVenue.pricePerHour,
        openingHours: newVenue.openingHours,
        operatorId: 'temp-operator-id',
        isActive: true,
        status: 'active',
        createdAt: new Date().toISOString()
      });
      setShowAddModal(false);
      setNewVenue({
        name: '',
        address: '',
        city: '',
        pricePerHour: 0,
        operatorName: '',
        machineCount: 0,
        tableCount: 0,
        openingHours: {
          monday: { open: '09:00', close: '22:00', closed: false },
          tuesday: { open: '09:00', close: '22:00', closed: false },
          wednesday: { open: '09:00', close: '22:00', closed: false },
          thursday: { open: '09:00', close: '22:00', closed: false },
          friday: { open: '09:00', close: '23:00', closed: false },
          saturday: { open: '10:00', close: '23:00', closed: false },
          sunday: { open: '10:00', close: '20:00', closed: false }
        }
      });
      await loadVenues();
    } catch (error) {
      console.error('Error adding venue:', error);
      alert('Hiba történt a helyszín hozzáadásakor.');
    }
  };

  const toggleStatus = async (id: string, currentStatus: string) => {
    try {
      await updateDoc(doc(db, 'venues', id), {
        status: currentStatus === 'active' ? 'inactive' : 'active'
      });
      await loadVenues();
    } catch (error) {
      console.error('Error updating venue:', error);
    }
  };

  const deleteVenue = async (id: string) => {
    if (window.confirm(t('venueManagement.confirmDelete'))) {
      try {
        await deleteDoc(doc(db, 'venues', id));
        await loadVenues();
      } catch (error) {
        console.error('Error deleting venue:', error);
      }
    }
  };

  const openEditModal = (venue: VenueData) => {
    setEditingVenue(venue);
    setShowEditModal(true);
  };

  const updateVenue = async () => {
    if (!editingVenue) return;

    // Validation
    if (!editingVenue.name || !editingVenue.address || !editingVenue.city) {
      alert('Név, cím és város megadása kötelező!');
      return;
    }
    if (!editingVenue.pricePerHour || editingVenue.pricePerHour <= 0) {
      alert('Óránkénti ár megadása kötelező és pozitív szám kell legyen!');
      return;
    }

    try {
      await updateDoc(doc(db, 'venues', editingVenue.id), {
        name: editingVenue.name,
        address: editingVenue.address,
        city: editingVenue.city,
        pricePerHour: editingVenue.pricePerHour,
        openingHours: editingVenue.openingHours
      });
      setShowEditModal(false);
      setEditingVenue(null);
      await loadVenues();
    } catch (error) {
      console.error('Error updating venue:', error);
      alert('Hiba történt a helyszín frissítésekor.');
    }
  };

  const filteredVenues = venues.filter(venue =>
    venue.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    venue.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    venue.operatorName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-[#B8B8C3]">{t('venueManagement.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      <div className="card-dark rounded-lg shadow p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gradient-cyan mb-2 whitespace-nowrap">{t('venueManagement.title')}</h2>
              <p className="text-[#B8B8C3]">{t('venueManagement.description')}</p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="hidden sm:flex px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors items-center gap-2 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('venueManagement.addVenue')}
              </button>
            )}
          </div>

          {isAdmin && (
            <button
              onClick={() => setShowAddModal(true)}
              className="sm:hidden w-full mb-4 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('venueManagement.addVenue')}
            </button>
          )}
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder={t('venueManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-[#010306] text-white placeholder-[#B8B8C3]"
            />
            <svg
              className="absolute left-3 top-3 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-[#31383D]">
            <thead className="bg-[#1d232a]">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase">
                  {t('venueManagement.venueName')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase hidden md:table-cell">
                  {t('venueManagement.address')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase hidden lg:table-cell">
                  {t('venueManagement.operator')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase">
                  {t('venueManagement.machines')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase">
                  {t('venueManagement.tables')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase hidden sm:table-cell">
                  {t('venueManagement.status')}
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-[#B8B8C3] uppercase">
                  {t('venueManagement.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#15191E] divide-y divide-[#31383D]">
              {filteredVenues.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-[#B8B8C3]">
                    {t('venueManagement.noVenues')}
                  </td>
                </tr>
              ) : (
                filteredVenues.map((venue) => (
                  <tr key={venue.id} className="hover:bg-[#1d232a]">
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-white truncate max-w-[120px]">{venue.name}</div>
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <div className="text-sm text-[#B8B8C3] truncate max-w-[150px]">{venue.address}</div>
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell">
                      <div className="text-sm text-[#B8B8C3] truncate max-w-[120px]">{venue.operatorName}</div>
                    </td>
                    <td className="px-3 py-3 text-sm text-[#B8B8C3] text-center">
                      {venue.machineCount}
                    </td>
                    <td className="px-3 py-3 text-sm text-[#B8B8C3] text-center">
                      {venue.tableCount}
                    </td>
                    <td className="px-3 py-3 hidden sm:table-cell">
                      {venue.status === 'active' ? (
                        <span className="badge-green">
                          {t('venueManagement.active')}
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-900/20 text-red-400 border border-red-500">
                          {t('venueManagement.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-medium">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => openEditModal(venue)}
                          className="px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold text-xs"
                          title={t('common.edit')}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => toggleStatus(venue.id, venue.status)}
                          className="px-2 py-1.5 bg-[#00AFF2] text-[#010306] rounded-lg hover:bg-[#2bc48a] transition-all font-semibold text-xs"
                          title={venue.status === 'active' ? t('venueManagement.deactivate') : t('venueManagement.activate')}
                        >
                          {venue.status === 'active' ? '⏸' : '▶'}
                        </button>
                        <button
                          onClick={() => deleteVenue(venue.id)}
                          className="px-2 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold text-xs"
                          title={t('common.delete')}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showEditModal && editingVenue && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="card-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">Helyszín szerkesztése</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    {t('venueManagement.venueName')}
                  </label>
                  <input
                    type="text"
                    value={editingVenue.name}
                    onChange={(e) => setEditingVenue({ ...editingVenue, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    {t('venueManagement.address')}
                  </label>
                  <input
                    type="text"
                    value={editingVenue.address}
                    onChange={(e) => setEditingVenue({ ...editingVenue, address: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    Város *
                  </label>
                  <input
                    type="text"
                    value={editingVenue.city}
                    onChange={(e) => setEditingVenue({ ...editingVenue, city: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                    placeholder="Budapest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    Ár/óra (Ft) *
                  </label>
                  <input
                    type="number"
                    value={editingVenue.pricePerHour || ''}
                    onChange={(e) => setEditingVenue({ ...editingVenue, pricePerHour: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                    placeholder="2500"
                  />
                </div>

                <div className="border-t border-[#31383D] pt-4">
                  <h4 className="text-sm font-medium text-white mb-3">Nyitvatartás</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(editingVenue.openingHours).map(([day, hours]) => (
                      <div key={day} className="grid grid-cols-4 gap-2 items-center">
                        <label className="text-xs text-[#B8B8C3] capitalize">{day}</label>
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => setEditingVenue({
                            ...editingVenue,
                            openingHours: {
                              ...editingVenue.openingHours,
                              [day]: { ...hours, open: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                          style={{ colorScheme: 'dark' }}
                        />
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => setEditingVenue({
                            ...editingVenue,
                            openingHours: {
                              ...editingVenue.openingHours,
                              [day]: { ...hours, close: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                          style={{ colorScheme: 'dark' }}
                        />
                        <label className="flex items-center gap-1 text-xs text-[#B8B8C3]">
                          <input
                            type="checkbox"
                            checked={hours.closed}
                            onChange={(e) => setEditingVenue({
                              ...editingVenue,
                              openingHours: {
                                ...editingVenue.openingHours,
                                [day]: { ...hours, closed: e.target.checked }
                              }
                            })}
                            className="rounded"
                          />
                          Zárva
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={updateVenue}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingVenue(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-[#B8B8C3] rounded-lg hover:bg-gray-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="card-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">{t('venueManagement.addVenue')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    {t('venueManagement.venueName')}
                  </label>
                  <input
                    type="text"
                    value={newVenue.name}
                    onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white placeholder-[#B8B8C3]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    {t('venueManagement.address')}
                  </label>
                  <input
                    type="text"
                    value={newVenue.address}
                    onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white placeholder-[#B8B8C3]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    Város *
                  </label>
                  <input
                    type="text"
                    value={newVenue.city}
                    onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                    placeholder="Budapest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-1">
                    Ár/óra (Ft) *
                  </label>
                  <input
                    type="number"
                    value={newVenue.pricePerHour || ''}
                    onChange={(e) => setNewVenue({ ...newVenue, pricePerHour: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 bg-[#010306] text-white"
                    placeholder="2500"
                  />
                </div>

                <div className="border-t border-[#31383D] pt-4">
                  <h4 className="text-sm font-medium text-white mb-3">Nyitvatartás</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {Object.entries(newVenue.openingHours).map(([day, hours]) => (
                      <div key={day} className="grid grid-cols-4 gap-2 items-center">
                        <label className="text-xs text-[#B8B8C3] capitalize">{day}</label>
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => setNewVenue({
                            ...newVenue,
                            openingHours: {
                              ...newVenue.openingHours,
                              [day]: { ...hours, open: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                          style={{ colorScheme: 'dark' }}
                        />
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => setNewVenue({
                            ...newVenue,
                            openingHours: {
                              ...newVenue.openingHours,
                              [day]: { ...hours, close: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                          style={{ colorScheme: 'dark' }}
                        />
                        <label className="flex items-center gap-1 text-xs text-[#B8B8C3]">
                          <input
                            type="checkbox"
                            checked={hours.closed}
                            onChange={(e) => setNewVenue({
                              ...newVenue,
                              openingHours: {
                                ...newVenue.openingHours,
                                [day]: { ...hours, closed: e.target.checked }
                              }
                            })}
                            className="rounded"
                          />
                          Zárva
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={addVenue}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.save')}
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-[#B8B8C3] rounded-lg hover:bg-gray-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueManagement;
